"use client";

import { createContext, useContext, useState, useCallback, useEffect, useMemo, lazy, Suspense, type ReactNode } from "react";

const TrialFormModal = lazy(() => import("@/components/forms/TrialFormModal"));

/** Which screen the modal opens on; null = closed. */
type ModalVariant = "chooser" | "form" | null;

interface TrialFormContext {
  openTrialForm: () => void;
}

const Ctx = createContext<TrialFormContext>({
  openTrialForm: () => {},
});

export function useTrialForm() {
  return useContext(Ctx);
}

export default function TrialFormProvider({ children }: { children: ReactNode }) {
  // Single source of truth for the modal, including which screen it shows —
  // the modal itself is stateless about this, so deep links and buttons can
  // switch screens without remounting it (typed form input survives no-ops).
  const [variant, setVariant] = useState<ModalVariant>(null);

  // Trial buttons show the two-option chooser (start with card / book a demo).
  const openTrialForm = useCallback(() => setVariant("chooser"), []);
  const openDemoForm = useCallback(() => setVariant("form"), []);
  const onClose = useCallback(() => setVariant(null), []);

  // Deep-link triggers, synced from external systems (cookie set by the proxy,
  // URL hash on load + in-page clicks): open_trial cookie and #get-started →
  // chooser; #book-a-demo → straight to the demo form. The hash is stripped so
  // the same link can re-fire after the modal is closed.
  useEffect(() => {
    if (document.cookie.split("; ").includes("open_trial=1")) {
      document.cookie = "open_trial=; max-age=0; path=/";
      setVariant("chooser");
    }
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash !== "#book-a-demo" && hash !== "#get-started") return;
      setVariant(hash === "#book-a-demo" ? "form" : "chooser");
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  // Stable context value — consumers (header, hero, CTAs) don't re-render when
  // the modal opens or closes.
  const ctx = useMemo(() => ({ openTrialForm }), [openTrialForm]);

  return (
    <Ctx value={ctx}>
      {children}
      {variant !== null && (
        <Suspense>
          <TrialFormModal view={variant} onShowForm={openDemoForm} onClose={onClose} />
        </Suspense>
      )}
    </Ctx>
  );
}
